import { Edit, Eye, Home, Trash2 } from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import TenantModal from './TenantModal';

const Tenants = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [modalMode, setModalMode] = useState('add'); // 'add', 'edit', 'view'
  const [selectedPropertyOwner, setSelectedPropertyOwner] = useState('');
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const queryClient = useQueryClient();

  // 사용자 정보 가져오기 (RMC 사용자 확인용)
  const { data: userInfo } = useQuery(['user-info'], () =>
    api.get('/auth/me'),
    {
      onSuccess: (data) => {
        setCurrentUser(data);
        // RMC 사용자가 일반 경로로 접속한 경우 RMC 전용 경로로 리다이렉트
        if (data?.role === 'rental_management_company') {
          navigate('/rmc/tenants', { replace: true });
        }
      }
    }
  );

  // 임대사업자 목록 가져오기 (RMC 사용자용)
  const { data: propertyOwners } = useQuery(
    ['property-owners', userInfo?.role],
    () => api.get('/property-owners'),
    {
      enabled: userInfo?.role === 'rental_management_company',
      staleTime: 5 * 60 * 1000, // 5분
      cacheTime: 10 * 60 * 1000, // 10분
    }
  );

  // 건물 목록 가져오기 (RMC 사용자는 임대사업자별, 임대사업자는 자신의 건물)
  const { data: buildings } = useQuery(
    ['buildings', selectedPropertyOwner, userInfo?.role],
    () => {
      if (userInfo?.role === 'rental_management_company') {
        // RMC 사용자는 선택된 임대사업자의 건물
        const url = selectedPropertyOwner
          ? `/buildings?property_owner_id=${selectedPropertyOwner}`
          : '/buildings';
        return api.get(url);
      } else {
        // 임대사업자 사용자는 자신의 건물
        return api.get('/buildings');
      }
    },
    {
      enabled: !!userInfo
    }
  );

  // 임차인 목록 조회
  const { data: tenantsData, isLoading, error } = useQuery(
    ['tenants', selectedPropertyOwner, selectedBuilding],
    () => {
      console.log('임차인 목록 조회 요청');
      const params = new URLSearchParams();
      if (selectedPropertyOwner) {
        params.append('property_owner_id', selectedPropertyOwner);
      }
      if (selectedBuilding) {
        params.append('building_id', selectedBuilding);
      }
      const url = params.toString() ? `/tenants?${params.toString()}` : '/tenants';
      return api.get(url);
    },
    {
      onSuccess: (data) => {
        console.log('임차인 목록 조회 성공:', data);
      },
      onError: (error) => {
        console.error('임차인 목록 조회 오류:', error);
        console.error('오류 상세:', error.response?.data);
      }
    }
  );

  const tenants = tenantsData?.tenants || [];

  // 임차인 삭제
  const deleteTenantMutation = useMutation(
    (tenantId) => api.delete(`/tenants/${tenantId}`),
    {
      onSuccess: () => {
        toast.success('임차인이 삭제되었습니다.');
        queryClient.invalidateQueries('tenants');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || '임차인 삭제에 실패했습니다.');
      }
    }
  );

  const handleDeleteTenant = (tenantId) => {
    if (window.confirm('정말로 이 임차인을 삭제하시겠습니까?')) {
      deleteTenantMutation.mutate(tenantId);
    }
  };


  const handleEditTenant = (tenant) => {
    setSelectedTenant(tenant);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleViewTenant = (tenant) => {
    setSelectedTenant(tenant);
    setModalMode('view');
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedTenant(null);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    queryClient.invalidateQueries('tenants');
  };

  // 임대사업자 변경 시 건물 선택 초기화
  const handlePropertyOwnerChange = (ownerId) => {
    setSelectedPropertyOwner(ownerId);
    setSelectedBuilding(''); // 건물 선택 초기화
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">로딩 중...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">임차인 목록을 불러오는데 실패했습니다.</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">임차인 관리</h1>
        <div className="text-sm text-gray-500">
          임차인은 계약을 통해 자동으로 생성됩니다
        </div>
      </div>

      {/* 필터 섹션 */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 임대사업자 필터 (RMC 사용자용) */}
          {userInfo?.role === 'rental_management_company' && (
            <div className="flex items-center gap-2">
              <Home className="h-5 w-5 text-gray-400" />
              <label className="text-sm font-medium text-gray-700">
                임대사업자:
              </label>
              <select
                value={selectedPropertyOwner}
                onChange={(e) => handlePropertyOwnerChange(e.target.value)}
                className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              >
                <option value="">전체 임대사업자</option>
                {propertyOwners?.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.company_name || owner.name} ({owner.buildings_count}개 건물)
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* 건물 필터 (모든 사용자용) */}
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-gray-400" />
            <label className="text-sm font-medium text-gray-700">
              건물:
            </label>
            <select
              value={selectedBuilding}
              onChange={(e) => setSelectedBuilding(e.target.value)}
              className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              disabled={userInfo?.role === 'rental_management_company' && !selectedPropertyOwner}
            >
              <option value="">전체 건물</option>
              {buildings?.map((building) => (
                <option key={building.id} value={building.id}>
                  {building.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  이름
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  연락처
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  이메일
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  직업
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  비상연락처
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  관리
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {tenants.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="text-gray-500 text-lg mb-4">등록된 임차인이 없습니다.</div>
                    <div className="text-sm text-gray-400">
                      임차인은 계약을 통해 자동으로 생성됩니다.
                    </div>
                  </td>
                </tr>
              ) : (
                tenants.map((tenant) => (
                  <tr key={tenant.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {tenant.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tenant.phone}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tenant.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{tenant.occupation || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {tenant.emergency_contact ? (
                          <>
                            {tenant.emergency_contact}
                            <br />
                            <span className="text-gray-500">{tenant.emergency_phone}</span>
                          </>
                        ) : (
                          '-'
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleViewTenant(tenant)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="상세보기"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleEditTenant(tenant)}
                          className="text-green-600 hover:text-green-900 p-1"
                          title="수정"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteTenant(tenant.id)}
                          className="text-red-600 hover:text-red-900 p-1"
                          title="삭제"
                          disabled={deleteTenantMutation.isLoading}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <TenantModal
          isOpen={showModal}
          tenant={selectedTenant}
          mode={modalMode}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}
    </div>
  );
};

export default Tenants;




